import type { CreateSigningRequestInput, Provider, SigningRequest } from "@vericreator/contracts";
import { Film, Instagram, Link2, LoaderCircle, ShieldCheck, Youtube } from "lucide-react";
import { useState } from "react";
import { ApiError, api } from "../../lib/api";

interface SigningComposerProps {
  readonly accessToken: string;
  onCreated(request: SigningRequest): void;
}

const providers: ReadonlyArray<{ id: Provider; label: string; Icon: typeof Youtube }> = [
  { id: "youtube", label: "YouTube", Icon: Youtube },
  { id: "tiktok", label: "TikTok", Icon: Film },
  { id: "instagram", label: "Instagram", Icon: Instagram }
];

export function SigningComposer({ accessToken, onCreated }: SigningComposerProps) {
  const [provider, setProvider] = useState<Provider>("youtube");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [sourceRightsConfirmed, setSourceRightsConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (!sourceRightsConfirmed) {
      setError("Confirme que você possui autorização para registrar esta obra.");
      return;
    }
    const input: CreateSigningRequestInput = {
      title: title.trim() || undefined,
      source: { kind: "public_url", url: url.trim(), preferredProvider: provider },
      sourceRightsConfirmed: true
    };

    setIsSubmitting(true);
    try {
      const request = await api.createSigningRequest(accessToken, input);
      onCreated(request);
      setTitle("");
      setUrl("");
      setSourceRightsConfirmed(false);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(messageForCode(caught.problem.code));
      } else {
        setError("Não foi possível criar o pedido. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="signing-composer" aria-labelledby="signing-composer-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Novo registro</span>
          <h2 id="signing-composer-title">Prepare uma assinatura</h2>
        </div>
        <span className="protocol-badge">V3 Compact Evidence · shadow</span>
      </div>

      <form className="signing-form" onSubmit={submit}>
        <div className="source-explainer">
          <Link2 size={17} />
          <span>O Studio coleta temporariamente a cópia pública para assinar e descarta o vídeo ao concluir. Somente as provas JSON ficam no registro.</span>
        </div>

        <label className="field">
          <span>Título interno <em>opcional</em></span>
          <input
            maxLength={160}
            placeholder="Ex.: Episódio 01 — corte oficial"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <div className="provider-picker" role="group" aria-label="Plataforma de origem">
          {providers.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={provider === id ? "provider-button active" : "provider-button"}
              type="button"
              onClick={() => setProvider(id)}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>
        <label className="field">
          <span>URL pública</span>
          <input
            required
            type="url"
            inputMode="url"
            placeholder="https://"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
          <small>O backend confirma a plataforma e somente libera fontes públicas suportadas.</small>
        </label>

        <label className="signing-attestation">
          <input
            checked={sourceRightsConfirmed}
            onChange={(event) => setSourceRightsConfirmed(event.target.checked)}
            type="checkbox"
          />
          <ShieldCheck size={17} />
          <span>Declaro que tenho autorização para registrar esta obra. Esta declaração não substitui a futura verificação de propriedade do canal.</span>
        </label>

        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button submit-button" disabled={isSubmitting} type="submit">
          {isSubmitting ? <LoaderCircle className="spin" size={18} /> : <span className="seal-dot" />}
          {isSubmitting ? "Enviando para assinatura…" : "Iniciar assinatura"}
        </button>
      </form>
    </section>
  );
}

function messageForCode(code: string): string {
  const messages: Record<string, string> = {
    invalid_source_url: "Informe uma URL pública HTTPS válida.",
    unsupported_source_provider: "Nesta etapa, use um link do YouTube, TikTok ou Instagram.",
    source_provider_mismatch: "A plataforma escolhida não corresponde ao link informado.",
    invalid_request: "Revise os dados do pedido e tente novamente.",
    upload_signing_not_available: "O piloto assina fontes públicas. O envio de arquivo será habilitado em uma etapa posterior."
  };
  return messages[code] ?? "Não foi possível criar o pedido. Tente novamente.";
}
